import { readSetting, writeSetting } from '../../infrastructure/storage/localStore';

/**
 * The cars this DEVICE has won (SPEC-010 PR-10.2).
 *
 * The twin of `application/game/leaderboard.js` beside it, deliberately: same
 * storage, same posture, same honesty about what "this device" means. Legends
 * has no backend and is not getting one (ADR-0004), so a collection is the cars
 * won in this browser, on this phone, and clearing site data forgets them. The
 * screens say so, exactly as the board does.
 *
 * IDS ARE STORED, NOT RECORDS. A copy of the car would freeze its figures at the
 * moment it was won, and this catalogue is corrected in place — `carSpecs.js`
 * carries provenance for every number precisely because they move. A reader who
 * won the Supra in August should see the horsepower it is published at today,
 * not the one it was published at then.
 *
 * Nothing sensitive: it is a list of car ids (CON-003).
 */

const COLLECTION = 'duel.collection';

/**
 * One id the app could act on, or nothing.
 *
 * The shape check is not defensive. This is parsed from a string a previous
 * build wrote, a hand-edited storage entry, or a future build with a different
 * shape — and an id is interpolated into a path (`/assets/content/cars/<id>`)
 * and into a URL, which is exactly the boundary the coding guidelines say to
 * parse at rather than to trust. A car id is kebab-case everywhere it appears:
 * in the URL, in the content JSON and on disk.
 */
const CAR_ID = /^[a-z0-9]+(?:-[a-z0-9]+)*$/u;

function readId(value) {
    return typeof value === 'string' && CAR_ID.test(value) ? value : null;
}

/**
 * The won ids, in the order they were won.
 *
 * Order is kept rather than sorted, because it is the only record of the LADDER
 * this device climbed — the third car won is the one that cost three thousand
 * points over a record. Nothing draws that today; re-sorting would throw it away
 * before anything could.
 *
 * Everything unreadable comes back as an empty collection rather than throwing,
 * which is the same asymmetry `readLeaderboard` takes: a collection that could
 * take the garage down is a worse outcome than a collection that forgot itself.
 */
export function readCollection() {
    try {
        const stored = JSON.parse(readSetting(COLLECTION) ?? '[]');

        return (Array.isArray(stored) ? stored : [])
            .map(readId)
            .filter(Boolean)
            // A duplicate is a bug upstream rather than a reader, and it would
            // otherwise count twice toward the rung the next car is quoted at.
            .filter((id, index, ids) => ids.indexOf(id) === index);
    } catch (error) {
        return [];
    }
}

/**
 * Add a car to the collection and answer the collection it produced.
 *
 * IDEMPOTENT. Winning a car twice cannot happen from the game — the pool a run
 * is dealt from already holds it — but a double-fired effect can, and a list
 * with the Supra in it twice would quote the next car a rung too high for ever.
 * The list is answered either way, so a caller never has to decide whether its
 * write was the one that counted.
 */
export function recordWin(carId) {
    const id = readId(carId);
    const collection = readCollection();

    if (!id || collection.includes(id)) {
        return collection;
    }

    const updated = [...collection, id];

    writeSetting(COLLECTION, JSON.stringify(updated));

    return updated;
}
